import graphql__145700b56d30f43d3c60 from "./__generated__/IncomingInvoiceViewDownloadPdfMutation.graphql.ts";
import graphql__8b16ed00a72392e30ac3 from "./__generated__/IncomingInvoiceViewOverrideCategoryMutation.graphql.ts";
import graphql__0cb3833d3b80b223aa52 from "./__generated__/IncomingInvoiceViewResetCategoryMutation.graphql.ts";
import graphql__49bf2f13eddde469e1e4 from "./__generated__/IncomingInvoiceViewQuery.graphql.ts";import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {graphql} from "react-relay";
import {useLazyLoadQuery, useRelayEnvironment} from "react-relay/hooks";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import NoData from "./presentational/NoData";
import ActionButton from "./presentational/button/ActionButton";
import PdfViewer from "./PdfViewer/PdfViewer";
import ExpenseInvoiceServicePeriod from "./ExpenseInvoiceServicePeriod";
import {formatAmount} from "../helper/revenueFormat";
import {EXPENSE_CATEGORIES, ExpenseCategory} from "../helper/expenseLabels";
import {formatAmountInCurrency} from "../helper/expenseInvoiceFormat";
import {formatDay} from "../helper/dateFormat";
import {EXPENSE_INVOICES_PATH, expenseInvoicesPath} from "../helper/expenseInvoiceFilter";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../contexts/ErrorContext";
import type {IncomingInvoiceViewQuery as IncomingInvoiceViewQueryType} from "./__generated__/IncomingInvoiceViewQuery.graphql";
import type {IncomingInvoiceViewDownloadPdfMutation} from "./__generated__/IncomingInvoiceViewDownloadPdfMutation.graphql";
import type {IncomingInvoiceViewOverrideCategoryMutation} from "./__generated__/IncomingInvoiceViewOverrideCategoryMutation.graphql";
import type {IncomingInvoiceViewResetCategoryMutation} from "./__generated__/IncomingInvoiceViewResetCategoryMutation.graphql";

const downloadPdfMutation = graphql__145700b56d30f43d3c60;

const overrideCategoryMutation = graphql__8b16ed00a72392e30ac3;

const resetCategoryMutation = graphql__0cb3833d3b80b223aa52;

/**
 * A single incoming invoice with its PDF and all its data. Expense category and service period
 * can be corrected here for this invoice alone.
 */
const IncomingInvoiceView: React.FC = () => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const {fastBillInvoiceId} = useParams();
    const [searchParams] = useSearchParams();

    const data = useLazyLoadQuery<IncomingInvoiceViewQueryType>(
        graphql__49bf2f13eddde469e1e4,
        {id: fastBillInvoiceId ?? ''},
        {fetchPolicy: 'store-and-network'}
    );
    const invoice = data.expenseInvoice;

    const [pdfUri, setPdfUri] = useState<string | undefined>(undefined);
    const [pdfFilename, setPdfFilename] = useState<string | undefined>(undefined);
    const [pdfUnavailable, setPdfUnavailable] = useState(false);
    const [pdfRetried, setPdfRetried] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const requestPdfDownload = useCallback((onUri: (uri: string | null, filename: string | null) => void) => {
        commitMutationWithErrorHandling<IncomingInvoiceViewDownloadPdfMutation>(
            relayEnvironment,
            {
                mutation: downloadPdfMutation,
                variables: {input: {id: invoice.id}},
                onCompleted: response => onUri(response.downloadExpenseInvoicePdf?.uri ?? null, response.downloadExpenseInvoicePdf?.filename ?? null),
                onError: () => onUri(null, null),
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToDownloadPdf'})}
        );
    }, [invoice.id, relayEnvironment, addError, intl]);

    useEffect(() => {
        setPdfUri(undefined);
        setPdfUnavailable(false);
        setPdfRetried(false);
    }, [invoice.id]);

    useEffect(() => {
        if (invoice.hasDocument && pdfUri === undefined && !pdfUnavailable) {
            requestPdfDownload((uri, filename) => {
                setPdfUri(uri ?? undefined);
                setPdfFilename(filename ?? undefined);
                setPdfUnavailable(!uri);
            });
        }
    }, [invoice.hasDocument, pdfUri, pdfUnavailable, requestPdfDownload]);

    // The link is only valid for a short time, so a single retry with a fresh link is attempted
    // before giving up.
    const handlePdfLoadError = (error: Error) => {
        console.error('Failed to load expense invoice PDF', error);
        if (pdfRetried) {
            setPdfUnavailable(true);
            return;
        }
        setPdfRetried(true);
        setPdfUri(undefined);
    };

    const handleRefreshPdf = () => {
        setPdfUri(undefined);
        setPdfUnavailable(false);
        setPdfRetried(false);
    };

    // The link shown in the viewer may have expired, so the download asks for a fresh one.
    const handleDownload = () => {
        setIsDownloading(true);
        requestPdfDownload(uri => {
            setIsDownloading(false);
            if (uri) {
                window.location.href = uri;
            }
        });
    };

    const changeCategory = (value: string) => {
        if (value === '') {
            commitMutationWithErrorHandling<IncomingInvoiceViewResetCategoryMutation>(
                relayEnvironment,
                {mutation: resetCategoryMutation, variables: {input: {id: invoice.id}}},
                addError,
                {customErrorMessage: intl.formatMessage({id: 'errors.failedToChangeExpenseInvoiceCategory'})}
            );
            return;
        }
        commitMutationWithErrorHandling<IncomingInvoiceViewOverrideCategoryMutation>(
            relayEnvironment,
            {
                mutation: overrideCategoryMutation,
                variables: {input: {id: invoice.id, expenseCategory: value as ExpenseCategory}},
                optimisticResponse: {overrideExpenseInvoiceCategory: {id: invoice.id, expenseCategory: value as ExpenseCategory, expenseCategoryOverride: value as ExpenseCategory}},
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToChangeExpenseInvoiceCategory'})}
        );
    };

    const listQuery = searchParams.toString();
    const isForeignCurrency = invoice.currency !== 'EUR';
    const amountInCurrency = (cents: number) => formatAmountInCurrency(intl, cents, invoice.currency);

    const details: Array<[string, React.ReactNode]> = [
        [intl.formatMessage({id: 'view.incomingInvoice.supplier'}), (
            <>
                <Link to={expenseInvoicesPath({supplier: invoice.supplierIdentifier})} className="text-gray-900 underline decoration-gray-300 hover:decoration-gray-700">{invoice.supplierName}</Link>
                {invoice.supplierSpelling !== invoice.supplierName && (
                    <div className="text-xs text-gray-500 whitespace-pre">„{invoice.supplierSpelling}“</div>
                )}
            </>
        )],
        [intl.formatMessage({id: 'view.incomingInvoices.col.invoiceNumber'}), invoice.invoiceNumber ?? '–'],
        [intl.formatMessage({id: 'view.incomingInvoices.col.invoiceDate'}), formatDay(intl, invoice.invoiceDate)],
        [intl.formatMessage({id: 'view.incomingInvoice.dueDate'}), formatDay(intl, invoice.dueDate)],
        [intl.formatMessage({id: 'view.incomingInvoices.col.paidDate'}), formatDay(intl, invoice.paidDate)],
        [intl.formatMessage({id: 'view.incomingInvoice.netAmountInEuro'}), formatAmount(invoice.netAmountInEuro)],
        [intl.formatMessage({id: 'view.incomingInvoice.netAmount'}), amountInCurrency(invoice.netAmount)],
        [intl.formatMessage({id: 'view.incomingInvoice.vatAmount'}), amountInCurrency(invoice.vatAmount)],
        [intl.formatMessage({id: 'view.incomingInvoice.grossAmount'}), amountInCurrency(invoice.grossAmount)],
        ...(isForeignCurrency ? [[
            intl.formatMessage({id: 'view.incomingInvoice.exchangeRate'}),
            intl.formatMessage({id: 'view.incomingInvoice.exchangeRateValue'}, {rate: intl.formatNumber(invoice.exchangeRate, {maximumFractionDigits: 6}), currency: invoice.currency}),
        ] as [string, React.ReactNode]] : []),
        [intl.formatMessage({id: 'view.incomingInvoice.fastBillInvoiceId'}), invoice.fastBillInvoiceId],
    ];

    return (
        <div className="space-y-4 px-4 sm:px-6 lg:px-0">
            <div className="flex flex-wrap items-center gap-3">
                <Link to={EXPENSE_INVOICES_PATH + (listQuery !== '' ? '?' + listQuery : '')} className="text-sm text-gray-500 hover:text-gray-900">
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-1"/>
                    {intl.formatMessage({id: 'view.incomingInvoice.back'})}
                </Link>
                <h2 className="text-lg font-medium text-gray-900">
                    {invoice.supplierName}{invoice.invoiceNumber ? ' · ' + invoice.invoiceNumber : ''}
                </h2>
                {invoice.deletedInFastBillAt && (
                    <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-bold leading-5 text-red-800">
                        {intl.formatMessage({id: 'view.incomingInvoice.deletedInFastBill'}, {date: formatDay(intl, invoice.deletedInFastBillAt)})}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                    <Panel fullWidthChildren={true}>
                        <PdfViewer
                            file={pdfUri}
                            filename={pdfFilename ?? (invoice.invoiceNumber ?? invoice.fastBillInvoiceId) + '.pdf'}
                            onDownload={handleDownload}
                            isDownloading={isDownloading}
                            noData={(<NoData
                                headline=""
                                description={(
                                    <>
                                        {!invoice.hasDocument
                                            ? intl.formatMessage({id: 'view.incomingInvoice.document.notArchived'})
                                            : pdfUnavailable
                                                ? intl.formatMessage({id: 'invoice.document.unavailable'})
                                                : intl.formatMessage({id: 'view.incomingInvoice.document.loading'})}
                                        {invoice.hasDocument && pdfUnavailable && (
                                            <div className="mt-4">
                                                <ActionButton label={intl.formatMessage({id: 'button.refresh'})} onClick={handleRefreshPdf}/>
                                            </div>
                                        )}
                                    </>
                                )}
                            />)}
                            error={(<NoData
                                headline=""
                                description={(
                                    <>
                                        {intl.formatMessage({id: 'invoice.document.loadFailed'})}
                                        <div className="mt-4">
                                            <ActionButton label={intl.formatMessage({id: 'button.refresh'})} onClick={handleRefreshPdf}/>
                                        </div>
                                    </>
                                )}
                            />)}
                            onLoadError={handlePdfLoadError}
                        />
                    </Panel>
                </div>
                <div className="lg:col-span-4 space-y-6">
                    <Panel headline={intl.formatMessage({id: 'view.incomingInvoice.details'})}>
                        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                            {details.map(([label, value]) => (
                                <React.Fragment key={label}>
                                    <dt className="text-gray-500">{label}</dt>
                                    <dd className="text-gray-900 text-right">{value}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                    </Panel>
                    <Panel headline={intl.formatMessage({id: 'view.incomingInvoices.col.expenseCategory'})}>
                        <select
                            value={invoice.expenseCategoryOverride ?? ''}
                            onChange={event => changeCategory(event.target.value)}
                            className="border border-gray-300 rounded pl-2 pr-8 py-1.5 text-sm text-gray-900 w-full"
                        >
                            <option value="">
                                {invoice.expenseCategoryOverride
                                    ? intl.formatMessage({id: 'view.incomingInvoice.category.ofSupplier'})
                                    : intl.formatMessage(
                                        {id: 'view.incomingInvoice.category.ofSupplierWithCategory'},
                                        {category: invoice.expenseCategory ? intl.formatMessage({id: `expense.category.${invoice.expenseCategory}`}) : '–'}
                                    )}
                            </option>
                            {EXPENSE_CATEGORIES.map(category => (
                                <option key={category} value={category}>{intl.formatMessage({id: `expense.category.${category}`})}</option>
                            ))}
                        </select>
                        <p className="mt-2 text-xs text-gray-500">
                            {invoice.expenseCategoryOverride
                                ? intl.formatMessage({id: 'view.incomingInvoice.category.overriddenHint'})
                                : intl.formatMessage({id: 'view.incomingInvoice.category.ofSupplierHint'})}
                        </p>
                    </Panel>
                    <ExpenseInvoiceServicePeriod invoice={data.expenseInvoice}/>
                </div>
            </div>
        </div>
    );
};

export default IncomingInvoiceView;
